import React, { useEffect, useRef, useState } from 'react'

// CountdownTimer — Phase 3 shared primitive used by Giveaways and Campaigns.
//
// Props:
//   targetDate    — ISO string | Date | number (ms). Required.
//   variant       — 'compact' (card) | 'large' (hero). Default 'compact'.
//   onExpire      — optional callback fired exactly once when the timer hits 0.
//   className     — passthrough.
//
// Visible labels are Azerbaijani:
//   gün / saat / dəq / san. When expired: "Vaxt bitib".

const pad = (n) => String(n).padStart(2, '0')

const computeDiff = (target) => {
    if (!target) return null
    const ts = new Date(target).getTime()
    if (!Number.isFinite(ts)) return null
    const ms = ts - Date.now()
    if (ms <= 0) return { ms: 0, days: 0, hours: 0, minutes: 0, seconds: 0, expired: true }
    const sec = Math.floor(ms / 1000)
    return {
        ms,
        days:    Math.floor(sec / 86400),
        hours:   Math.floor((sec % 86400) / 3600),
        minutes: Math.floor((sec % 3600) / 60),
        seconds: sec % 60,
        expired: false,
    }
}

const CountdownTimer = ({
    targetDate,
    variant = 'compact',
    onExpire,
    className = '',
}) => {
    const [diff, setDiff] = useState(() => computeDiff(targetDate))
    const firedRef = useRef(false)

    useEffect(() => {
        // Reset firing guard when target changes.
        firedRef.current = false
        setDiff(computeDiff(targetDate))

        if (!targetDate) return undefined
        const t = setInterval(() => setDiff(computeDiff(targetDate)), 1000)
        return () => clearInterval(t)
    }, [targetDate])

    useEffect(() => {
        if (diff?.expired && !firedRef.current) {
            firedRef.current = true
            onExpire?.()
        }
    }, [diff?.expired, onExpire])

    if (!diff) {
        // No target supplied — render nothing.
        return null
    }

    if (diff.expired) {
        return (
            <span className={`inline-flex items-center gap-1.5 rounded-pill bg-slate-100 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-slate-500 ${className}`}>
                Vaxt bitib
            </span>
        )
    }

    if (variant === 'large') {
        // Cells shrink to 56px on the tightest viewports so 4 cells + gaps
        // never overflow a 320 px hero card. Wrapping enabled as a final fallback.
        const cell = 'flex flex-col items-center justify-center rounded-card bg-white/10 border border-white/15 px-2.5 sm:px-2 py-2 min-w-[56px] sm:min-w-[52px] backdrop-blur'
        return (
            <div
                role='timer'
                aria-label='Geri sayım'
                className={`inline-flex flex-wrap items-center gap-2 sm:gap-1.5 ${className}`}
            >
                <div className={cell}>
                    <span className='text-2xl sm:text-xl font-black leading-none tabular-nums'>{diff.days}</span>
                    <span className='mt-0.5 text-[10px] uppercase tracking-wider opacity-80'>gün</span>
                </div>
                <div className={cell}>
                    <span className='text-2xl sm:text-xl font-black leading-none tabular-nums'>{pad(diff.hours)}</span>
                    <span className='mt-0.5 text-[10px] uppercase tracking-wider opacity-80'>saat</span>
                </div>
                <div className={cell}>
                    <span className='text-2xl sm:text-xl font-black leading-none tabular-nums'>{pad(diff.minutes)}</span>
                    <span className='mt-0.5 text-[10px] uppercase tracking-wider opacity-80'>dəq</span>
                </div>
                <div className={cell}>
                    <span className='text-2xl sm:text-xl font-black leading-none tabular-nums'>{pad(diff.seconds)}</span>
                    <span className='mt-0.5 text-[10px] uppercase tracking-wider opacity-80'>san</span>
                </div>
            </div>
        )
    }

    // compact (card-friendly)
    return (
        <span
            role='timer'
            aria-label='Geri sayım'
            className={`inline-flex items-center gap-1 rounded-pill bg-accent-50 px-2.5 py-1 text-[11px] font-bold text-accent-700 tabular-nums ${className}`}
        >
            {diff.days > 0
                ? <>{diff.days} g?n ? {pad(diff.hours)}:{pad(diff.minutes)}:{pad(diff.seconds)}</>
                : <>{pad(diff.hours)}:{pad(diff.minutes)}:{pad(diff.seconds)}</>}
        </span>
    )
}

export default CountdownTimer
